import React, { useEffect, Children } from 'react';
import { motion } from 'framer-motion';
import { Clock, CheckCircle2, XCircle, ArrowRight, FileSearch, Eye, Sparkles } from 'lucide-react';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.16, 1, 0.3, 1]
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
export function ConveyancingPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <main className="relative z-10 bg-brand-black min-h-screen selection:bg-brand-orange/30" data-id="element-593">
      {/* 1. HERO SECTION */}
      <section className="relative pt-40 pb-32 md:pt-52 md:pb-40 overflow-hidden" data-id="element-594">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-brand-orange/5 rounded-full blur-[120px] pointer-events-none" data-id="element-595" />

        <div className="max-w-5xl mx-auto px-6 relative z-10 text-center" data-id="element-596">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: [0.16, 1, 0.3, 1]
        }} className="text-5xl md:text-7xl font-display mb-8 tracking-tight leading-[0.95] text-balance" data-id="element-597">
            High-Volume Conveyancing <br className="hidden md:block" data-id="element-598" />
            <span className="text-brand-orange" data-id="element-599">
              Without the Administrative Burnout.
            </span>
          </motion.h1>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.1,
          ease: [0.16, 1, 0.3, 1]
        }} className="max-w-3xl mx-auto space-y-6 mb-12" data-id="element-600">
            <p className="text-lg md:text-xl text-gray-400 leading-relaxed font-light max-w-2xl mx-auto" data-id="element-601">
              Stop letting routine document chasing and client updates throttle
              your firm's capacity. We deploy autonomous AI agents that handle
              the repetitive heavy lifting of file management and client
              communication, so your solicitors can focus on the law.
            </p>
            <p className="text-lg md:text-xl text-white font-medium" data-id="element-602">
              If your team is spending more time chasing than conveyancing, you
              have an automation problem.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2,
          ease: [0.16, 1, 0.3, 1]
        }} className="flex flex-col sm:flex-row items-center justify-center gap-6" data-id="element-603">
            <a href="mailto:sales@aijentik.com" className="w-full sm:w-auto bg-brand-orange hover:bg-brand-deep-orange text-white px-8 py-4 rounded-full font-medium transition-all duration-300 shadow-[0_0_40px_rgba(219,127,74,0.3)] hover:shadow-[0_0_60px_rgba(219,127,74,0.4)] flex items-center justify-center gap-2" data-id="element-604">
              Get in Touch
              <ArrowRight className="w-4 h-4" data-id="element-605" />
            </a>
            <a href="#use-cases" className="w-full sm:w-auto text-white hover:text-brand-orange px-8 py-4 font-medium transition-colors flex items-center justify-center" data-id="element-606">
              See the Use Cases
            </a>
          </motion.div>
        </div>
      </section>

      {/* 2. INDUSTRY REALITY SECTION */}
      <section className="py-32 bg-[#050505] border-y border-white/5" data-id="element-607">
        <div className="max-w-7xl mx-auto px-6" data-id="element-608">
          <div className="flex flex-col lg:flex-row gap-16 lg:gap-24 items-center" data-id="element-609">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={fadeUp} className="lg:w-1/2" data-id="element-610">
              <h2 className="text-4xl md:text-5xl font-display leading-[1.0] mb-6 text-balance tracking-tight" data-id="element-611">
                You're Not Just Practicing Law.
                <br data-id="element-612" />
                <span className="text-gray-500" data-id="element-613">
                  You're Managing a Paperwork Factory.
                </span>
              </h2>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={staggerContainer} className="lg:w-1/2" data-id="element-614">
              <motion.p variants={fadeUp} className="text-xl text-gray-400 leading-relaxed mb-10 font-light" data-id="element-615">
                Most conveyancing firms aren't limited by their legal expertise
                — they're capped by their administrative bandwidth. Clients
                calling for updates, documents stuck in email chains, and
                paralegals buried in data entry instead of progressing files.
              </motion.p>

              <div className="space-y-8" data-id="element-616">
                {[{
                label: 'The Update Trap',
                desc: 'Clients call and email constantly for status updates, consuming hours of staff time every day.'
              }, {
                label: 'The Document Bottleneck',
                desc: 'Critical documents sit in inboxes waiting to be chased, reviewed, and filed — slowing every file.'
              }, {
                label: 'The Capacity Ceiling',
                desc: 'Your best people spend 60% of their time on admin, not legal work — limiting how many files you can run.'
              }].map((point, i) => <motion.div key={i} variants={fadeUp} className="flex items-start gap-4" data-id="element-617">
                    <div className="mt-1 bg-red-500/10 p-1 rounded-full shrink-0" data-id="element-618">
                      <XCircle className="w-5 h-5 text-red-500/70" data-id="element-619" />
                    </div>
                    <div data-id="element-620">
                      <p className="text-lg text-white font-medium mb-1" data-id="element-621">
                        {point.label}
                      </p>
                      <p className="text-gray-400 font-light leading-relaxed" data-id="element-622">
                        {point.desc}
                      </p>
                    </div>
                  </motion.div>)}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 3. SOLUTION SECTION (AI AGENTS) */}
      <section id="use-cases" className="py-32 relative" data-id="element-623">
        <div className="max-w-7xl mx-auto px-6" data-id="element-624">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUp} className="max-w-3xl mb-20" data-id="element-625">
            <h2 className="text-4xl md:text-5xl font-display mb-6 leading-[1.0] tracking-tight text-balance" data-id="element-626">
              Purpose-Built Agents for High-Stakes Workflows.
            </h2>
            <p className="text-xl text-gray-400 font-light leading-relaxed max-w-2xl" data-id="element-627">
              We don't sell AI tools. We build autonomous digital team members
              that understand conveyancing law, access your case management
              system, and execute real tasks without human involvement.
            </p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="grid md:grid-cols-3 gap-8" data-id="element-628">
            {[{
            icon: <Eye className="w-6 h-6 text-brand-orange" data-id="element-629" />,
            title: 'Monitor the File',
            desc: 'Agents continuously track every matter — outstanding documents, upcoming deadlines, and client milestones — so nothing falls through the cracks.'
          }, {
            icon: <CheckCircle2 className="w-6 h-6 text-brand-orange" data-id="element-630" />,
            title: 'Take Action',
            desc: 'From sending document requests to updating clients on progress, agents complete tasks autonomously within your existing systems.'
          }, {
            icon: <Clock className="w-6 h-6 text-brand-orange" data-id="element-631" />,
            title: '24/7 Transparency',
            desc: 'Clients get instant, accurate updates any time of day — no more Monday morning voicemail queues or anxious follow-up emails.'
          }].map((card, i) => <motion.div key={i} variants={fadeUp} className="bg-white/[0.02] border border-white/5 p-10 rounded-2xl hover:bg-white/[0.04] transition-all duration-700 ease-premium hover:-translate-y-2 hover:border-brand-orange/30 hover:shadow-[0_20px_40px_-15px_rgba(219,127,74,0.15)] group" data-id="element-632">
                <div className="bg-brand-orange/10 w-12 h-12 rounded-xl flex items-center justify-center mb-8 group-hover:bg-brand-orange/20 transition-colors duration-500" data-id="element-633">
                  {card.icon}
                </div>
                <h3 className="text-xl font-medium text-white mb-4 tracking-tight" data-id="element-634">
                  {card.title}
                </h3>
                <p className="text-gray-400 leading-relaxed font-light" data-id="element-635">
                  {card.desc}
                </p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* 4. TRANSFORMATION SECTION (BEFORE vs AFTER) */}
      <section className="py-32 bg-[#050505] border-y border-white/5" data-id="element-636">
        <div className="max-w-7xl mx-auto px-6" data-id="element-637">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="grid lg:grid-cols-2 gap-12 lg:gap-0 relative" data-id="element-638">
            <div className="hidden lg:block absolute top-0 bottom-0 left-1/2 w-px bg-gradient-to-b from-transparent via-white/10 to-transparent -translate-x-1/2" data-id="element-639" />

            {/* Left: Traditional Model */}
            <motion.div variants={fadeUp} className="lg:pr-20" data-id="element-640">
              <h3 className="text-sm font-bold tracking-[0.25em] text-gray-500 uppercase mb-12" data-id="element-641">
                Traditional Model
              </h3>
              <div className="space-y-10" data-id="element-642">
                {['Monday morning: 47 voicemails, 80 emails, zero files progressed', 'Paralegal spends 3 hours chasing a single document via email', 'Data entered manually into CMS, then re-entered into forms'].map((text, i) => <div key={i} className="flex gap-6 opacity-60" data-id="element-643">
                    <XCircle className="w-6 h-6 text-gray-500 shrink-0" data-id="element-644" />
                    <p className="text-xl text-gray-400 font-light" data-id="element-645">{text}</p>
                  </div>)}
              </div>
            </motion.div>

            {/* Right: Aijentik Model */}
            <motion.div variants={fadeUp} className="lg:pl-20" data-id="element-646">
              <h3 className="text-sm font-bold tracking-[0.25em] text-brand-orange uppercase mb-12" data-id="element-647">
                Aijentik Model
              </h3>
              <div className="space-y-10" data-id="element-648">
                {['Clients received proactive weekend updates — Monday starts clean', 'Documents requested, chased, and filed autonomously overnight', 'Data captured once, populated everywhere instantly'].map((text, i) => <div key={i} className="flex gap-6" data-id="element-649">
                    <Sparkles className="w-6 h-6 text-brand-orange shrink-0" data-id="element-650" />
                    <p className="text-xl text-white font-medium" data-id="element-651">{text}</p>
                  </div>)}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* 5. USE CASES SECTION */}
      <section className="py-32" data-id="element-652">
        <div className="max-w-7xl mx-auto px-6" data-id="element-653">
          <motion.h2 initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUp} className="text-4xl md:text-5xl font-display text-center mb-20 leading-[1.0] tracking-tight text-balance" data-id="element-654">
            What This Actually Looks Like <br className="hidden md:block" data-id="element-655" />
            <span className="text-gray-500" data-id="element-656">in Your Firm</span>
          </motion.h2>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="grid md:grid-cols-3 gap-6" data-id="element-657">
            {[{
            title: '24/7 Client Update Agent',
            desc: 'An autonomous agent that monitors every file and sends clients accurate, personalised progress updates via email or SMS — before they even think to call.'
          }, {
            title: 'Document Chaser',
            desc: 'Automatically identifies outstanding documents, sends polite follow-ups to the right parties, and logs everything back into your case management system.'
          }, {
            title: 'Intake Specialist',
            desc: 'Captures new enquiries, qualifies leads, collects initial documents and ID verification, and creates the file in your CMS — all without human involvement.'
          }].map((card, i) => <motion.div key={i} variants={fadeUp} className="bg-[#0A0A0A] border border-white/10 p-10 rounded-2xl transition-all duration-700 ease-premium hover:-translate-y-2 hover:border-white/20 hover:shadow-2xl" data-id="element-658">
                <h3 className="text-2xl font-display mb-4 tracking-tight" data-id="element-659">
                  {card.title}
                </h3>
                <p className="text-gray-400 leading-relaxed font-light" data-id="element-660">
                  {card.desc}
                </p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* 6. METHODOLOGY SECTION */}
      <section className="py-32 bg-[#050505] border-y border-white/5" data-id="element-661">
        <div className="max-w-7xl mx-auto px-6" data-id="element-662">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUp} className="text-center mb-20" data-id="element-663">
            <h2 className="text-4xl md:text-5xl font-display mb-6 leading-[1.0] tracking-tight text-balance" data-id="element-664">
              No Fluff. Just Precision.
            </h2>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="grid md:grid-cols-3 gap-12 relative" data-id="element-665">
            <div className="hidden md:block absolute top-8 left-[15%] right-[15%] h-px bg-white/10" data-id="element-666" />

            {[{
            step: '01',
            title: 'Audit',
            desc: 'We map your current file lifecycle — from intake to settlement — and identify the exact friction points costing you time and capacity.'
          }, {
            step: '02',
            title: 'Build',
            desc: "We design and train custom agents on your firm's specific processes, tone of voice, compliance requirements, and case management system."
          }, {
            step: '03',
            title: 'Integration',
            desc: 'We connect agents directly to your CMS, email, and communication channels — going live with zero disruption to active files.'
          }].map((item, i) => <motion.div key={i} variants={fadeUp} className="relative z-10 flex flex-col items-center text-center group" data-id="element-667">
                <div className="w-16 h-16 rounded-full bg-brand-black border border-white/10 flex items-center justify-center text-xl font-display text-brand-orange mb-8 transition-all duration-500 group-hover:border-brand-orange/50 group-hover:bg-brand-orange/5 group-hover:shadow-[0_0_30px_rgba(219,127,74,0.2)]" data-id="element-668">
                  {item.step}
                </div>
                <h3 className="text-2xl font-medium text-white mb-4 tracking-tight" data-id="element-669">
                  {item.title}
                </h3>
                <p className="text-gray-400 font-light leading-relaxed max-w-sm" data-id="element-670">
                  {item.desc}
                </p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* 7. FINAL CTA SECTION */}
      <section className="py-32 relative overflow-hidden" data-id="element-671">
        <div className="absolute inset-0 bg-brand-orange/5" data-id="element-672" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-brand-orange/10 rounded-full blur-[100px] pointer-events-none" data-id="element-673" />

        <div className="max-w-4xl mx-auto px-6 relative z-10 text-center" data-id="element-674">
          <motion.h2 initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeUp} className="text-5xl md:text-6xl font-display mb-8 leading-[1.0] tracking-tight text-balance" data-id="element-675">
            Stop Managing Paperwork. <br data-id="element-676" />
            <span className="text-brand-orange" data-id="element-677">Start Scaling Your Firm.</span>
          </motion.h2>

          <motion.p initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeUp} className="text-xl text-gray-300 mb-12 font-light max-w-2xl mx-auto" data-id="element-678">
            Let's look at your current caseload and identify automation
            opportunities that free your team to focus on what matters.
          </motion.p>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeUp} className="flex flex-col items-center" data-id="element-679">
            <a href="mailto:sales@aijentik.com" className="inline-block bg-white text-brand-black hover:bg-gray-100 px-10 py-5 rounded-full font-medium text-lg transition-all duration-300 shadow-xl hover:shadow-2xl hover:-translate-y-1 mb-6" data-id="element-680">
              Contact Us — sales@aijentik.com
            </a>
            <p className="text-sm text-gray-500 font-medium" data-id="element-681">
              We'll get back to you within 24 hours.
            </p>
          </motion.div>
        </div>
      </section>
    </main>;
}